import { del } from 'idb-keyval';
import {
	type CSSProperties,
	type MouseEvent,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import { LocalizedCaseUrl } from 'src/easyclaim.api.client.typescript/src';
import { IconArrowRight } from 'src/easyclaim.app.assets';
import { IMAGE_CACHE_KEY } from 'src/easyclaim.app.contexts/ImageQueryPersister';
import { reportError } from 'src/easyclaim.app.libs/reportError';

import { FlagIcon } from './FlagIcon';
import styles from './styles/UpdatedLangChoose.module.css';

interface UpdatedLangChooseProps {
	currentLang: string;
	localizedCaseUrls: LocalizedCaseUrl[] | null | undefined;
}

export const UpdatedLangChoose = ({ currentLang, localizedCaseUrls }: UpdatedLangChooseProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
	const ref = useRef<HTMLDivElement>(null);

	const currentLangUrl = localizedCaseUrls?.find((l) => l.twoLetterLangCode === currentLang);

	const updatePosition = useCallback(() => {
		if (!ref.current) return;
		const rect = ref.current.getBoundingClientRect();

		setDropdownStyle({
			position: 'fixed',
			top: rect.bottom + 4,
			right: window.innerWidth - rect.right,
		});
	}, []);

	useEffect(() => {
		if (!isOpen) return;
		updatePosition();
		window.addEventListener('resize', updatePosition);
		window.addEventListener('scroll', updatePosition, true);

		return () => {
			window.removeEventListener('resize', updatePosition);
			window.removeEventListener('scroll', updatePosition, true);
		};
	}, [isOpen, updatePosition]);

	const handleLangClick = useCallback(
		(e: MouseEvent, l: LocalizedCaseUrl) => {
			e.stopPropagation();
			if (l.twoLetterLangCode === currentLang) {
				setIsOpen(false);

				return;
			}
			setIsOpen(false);
			void (async () => {
				try {
					await del(IMAGE_CACHE_KEY);
					window.location.href = l.url!;
				} catch (error) {
					reportError(error, { context: 'UpdatedLangChoose.handleLangClick' });
					window.location.href = l.url!;
				}
			})();
		},
		[currentLang]
	);

	return (
		<>
			<div ref={ref} className={styles.langChoose} onClick={() => setIsOpen(!isOpen)}>
				<FlagIcon
					className={styles.flagIcon}
					flagCountryCode={currentLangUrl?.twoLetterFlagCountryCode ?? currentLang}
				/>
				<IconArrowRight
					className={`${styles.arrowIcon}${isOpen ? ` ${styles.arrowIconOpen}` : ''}`}
				/>
			</div>
			{isOpen &&
				createPortal(
					<div className={styles.availableLangs} style={dropdownStyle}>
						{localizedCaseUrls?.map((l) => (
							<a
								key={l.twoLetterLangCode}
								className={l.twoLetterLangCode === currentLang ? styles.activeLang : undefined}
								onClick={(e) => handleLangClick(e, l)}
							>
								<FlagIcon
									flagCountryCode={l.twoLetterFlagCountryCode ?? l.twoLetterLangCode ?? ''}
								/>
								<span className={styles.lettersSpan}>{l.twoLetterLangCode?.toUpperCase()}</span>
							</a>
						))}
					</div>,
					document.body
				)}
		</>
	);
};
